import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSalvcarContext } from '@/app/contexts/SalvcarContext'
import { useSalvcarMutations } from '@/app/hooks/useSalvcarMutations'
import { useGetTransactionCommission } from '@/app/hooks/useGetTransactionCommission'
import { useContextSalvcar } from '@/app/hooks/useContextSalvcar'
import { useToast } from '@/view/components/ui/use-toast'
import { createCustomer } from '@/app/services/customerService/createCustomer'
import { useAuth } from '@/app/hooks/useAuth'

const getVehicleFormSchema = z.object({
  stateId: z.string().min(1, 'Estado é obrigatório'),
  cityId: z.string().min(1, 'Cidade é obrigatório'),
  typeVehicle: z.string().min(1, 'Tipos de veículo é obrigatório'),
  vehicle: z.string().min(1, 'Marca do veículo é obrigatório'),
  year: z.string().min(1, 'Ano do veículo é obrigatório'),
  modelId: z.string().min(1, 'Modelo do veículo é obrigatório'),
  plate: z.string().min(1, 'Placa do veículo é obrigatório'),
  description: z.string().optional(),
})

type getVehicleFormSchemaData = z.infer<typeof getVehicleFormSchema>

interface useVehicleFormModalProps {
  onContinue?: (data: getVehicleFormSchemaData) => void
}

export default function useVehicleFormModal({
  onContinue,
}: useVehicleFormModalProps) {
  const getVehicleForm = useForm<getVehicleFormSchemaData>({
    resolver: zodResolver(getVehicleFormSchema),
    mode: 'onChange',
  })

  const { customerData } = useSalvcarContext()
  const { toast } = useToast()
  const { makeProposalMutation } = useSalvcarMutations()
  const { user } = useAuth()

  const { watch, handleSubmit } = getVehicleForm

  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [pin, setPin] = useState(['', '', '', ''])

  const plate = watch('plate')
  const typeVehicle = watch('typeVehicle')
  const vehicle = watch('vehicle')
  const year = watch('year')
  const stateId = watch('stateId')
  const cityId = watch('cityId')

  const { data: commisionData, isLoading: isLoadingCommisionData } =
    useGetTransactionCommission({ type: 'SALVCAR' })

  const { data: brandData, isLoading: isLoadingBrandData } = useContextSalvcar(
    typeVehicle
      ? {
          type: 'B',
          typeVehicle: typeVehicle as 'C' | 'M' | 'TM',
        }
      : undefined,
  )

  const brandOptions =
    brandData?.map((item) => ({
      label: item.text,
      value: item.id.toString(),
    })) ?? []

  const { data: yearData, isLoading: isLoadingYearData } = useContextSalvcar(
    vehicle
      ? {
          type: 'Y',
          brandId: Number(vehicle),
        }
      : undefined,
  )

  const yearOptions =
    yearData?.map((item) => ({
      label: item.text,
      value: item.id.toString(),
    })) ?? []

  const { data: modelData, isLoading: isLoadingModelData } = useContextSalvcar(
    vehicle && year
      ? {
          type: 'V',
          brandId: Number(vehicle),
          year: Number(year),
        }
      : undefined,
  )

  const modelOptions =
    modelData?.map((item) => ({
      label: item.text,
      value: item.id.toString(),
    })) ?? []

  const { data: stateData, isLoading: isLoadingStateData } = useContextSalvcar({
    type: 'S',
  })

  const stateOptions =
    stateData?.map((item) => ({
      label: item.text,
      value: item.id.toString(),
    })) ?? []

  const { data: cityData, isLoading: isLoadingCityData } = useContextSalvcar(
    stateId
      ? {
          type: 'C',
          stateId: Number(stateId),
        }
      : undefined,
  )

  const cityOptions =
    cityData?.map((item) => ({
      label: item.text,
      value: item.id.toString(),
    })) ?? []

  const itemsTypeVehicle = [
    { label: 'Carro', value: 'C' },
    { label: 'Moto', value: 'M' },
    { label: 'Caminhão/Micro-ônibus', value: 'TM' },
  ]

  const handleOpenConfirmModal = handleSubmit(() => {
    setIsConfirmModalOpen(true)
  })

  const handleSendProposal = async () => {
    const formData = getVehicleForm.getValues()

    setIsConfirmModalOpen(false)
    setIsLoading(true)

    try {
      const cpfClean = customerData?.cpf.replace(/\D/g, '')
      let customerId: string | undefined

      const newCustomer = await createCustomer({
        name: customerData?.name || '',
        cpf: cpfClean || '',
        phone: customerData?.phone.replace(/\D/g, '') || '',
        email: customerData?.email || '',
      })

      customerId = newCustomer._id

      const pinJoin = pin ? pin.join('') : ''

      const payload = {
        name: customerData?.name ?? '',
        phone: customerData?.phone?.replace(/\D/g, '') ?? '',
        document: cpfClean ?? '',
        description: formData.description,
        vehicle:
          brandOptions.find((o) => o.value === formData.vehicle)?.label ?? '',
        model_id: formData.modelId,
        year: formData.year,
        plate: formData.plate,
        cityId: Number(formData.cityId),
        stateId: Number(formData.stateId),
        cityName:
          cityOptions.find((o) => o.value === formData.cityId)?.label ?? '',
        stateName:
          stateOptions.find((o) => o.value === formData.stateId)?.label ?? '',
        customerId,
        pin: pinJoin || undefined,
      }

      await makeProposalMutation.mutateAsync(payload)

      onContinue?.(formData)
    } catch (error: any) {
      toast({
        title: 'Erro ao gerar proposta',
        description:
          error?.message || 'Ocorreu um problema ao processar a proposta.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    form: getVehicleForm,
    isLoading,
    stateOptions,
    isLoadingStateData,
    stateId,
    cityOptions,
    isLoadingCityData,
    itemsTypeVehicle,
    isLoadingTypeVehicleData: false,
    typeVehicle,
    isLoadingBrandData,
    brandOptions,
    vehicle,
    isLoadingYearData,
    yearOptions,
    year,
    isLoadingModelData,
    modelOptions,
    commisionData,
    isLoadingCommisionData,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    handleOpenConfirmModal,
    handleSendProposal,
    pin,
    setPin,
    user,
    plate,
    cityId,
  }
}
